import React from 'react';
import { Input, Radio } from 'antd';
import { AllState } from 'constants/common';
import type { GovEnrollmentByState, PracticeRequirementsDto } from '../types';
import MedicaidIntro from './MedicaidIntro';
import MedicaidEnrollmentFields from './MedicaidEnrollmentFields';
import type { TMedicaidDerive } from './seed';
import s from './s.module.less';

export const stateName = (code: string) => AllState.find((i) => i.dictKey === code)?.content || code;

interface Props {
    // practice payers 里 Medicaid 所在的州（两字母州码，和 payer 的 state 写法一致）
    states: string[];
    value?: PracticeRequirementsDto | null;
    onChange: (value: PracticeRequirementsDto) => void;
    // practice 的实体类型（operating agreement 只在有实体时问）
    showOrgAgreement?: boolean;
    derive?: TMedicaidDerive;
}

// 原型 mcdGroupFields（09/21，10/19）：每个 Medicaid 州问一次「practice 是否已以 group 身份注册」，
// Yes 要 group Medicaid ID；任一州 No 时下面打开 practice 级注册问卷（答一次，所有州共用）。
// Set up your practice、group Review 和 Settings 的编辑态共用这一份
export default function MedicaidGroupFields({ states, value, onChange, showOrgAgreement, derive }: Props) {
    const medicaid: GovEnrollmentByState[] = value?.medicaid || [];
    const entry = (st: string) => medicaid.find((m) => m.state === st);
    const patchState = (st: string, patch: Partial<GovEnrollmentByState>) => {
        const others = medicaid.filter((m) => m.state !== st);
        onChange({ ...value, medicaid: [...others, { ...(entry(st) || { state: st }), ...patch }] });
    };
    const anyNo = states.some((st) => entry(st)?.enrolled === false);

    return (
        <>
            {states.map((st) => {
                const e = entry(st);
                let radio: 'Yes' | 'No' | undefined;
                if (e?.enrolled === true) radio = 'Yes';
                else if (e?.enrolled === false) radio = 'No';
                return (
                    <div className={s.question} key={st}>
                        <p className={s.questionText}>
                            Is the practice already enrolled in {stateName(st)} Medicaid as a group?
                        </p>
                        <Radio.Group
                            value={radio}
                            onChange={(ev) => patchState(st, { enrolled: ev.target.value === 'Yes' })}
                        >
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                        {e?.enrolled === true && (
                            <div className={s.groupIdField}>
                                <span className={s.label}>{stateName(st)} group Medicaid ID</span>
                                <Input
                                    value={e.certificationNumber || ''}
                                    placeholder="Do not enter spaces"
                                    onChange={(ev) =>
                                        patchState(st, { certificationNumber: ev.target.value.replace(/\s/g, '') })
                                    }
                                />
                            </div>
                        )}
                    </div>
                );
            })}
            {anyNo && (
                <div className={s.groupPanel}>
                    <MedicaidIntro
                        title="We will enroll your practice in Medicaid as a group"
                        text="The enrollment application needs these details about your practice. Answer them once and we use them for every state you enroll in."
                    />
                    <MedicaidEnrollmentFields
                        variant="group"
                        isCA={states.includes('CA')}
                        showOrgAgreement={showOrgAgreement}
                        derive={derive}
                        value={value?.medicaidEnrollment}
                        onChange={(medicaidEnrollment) => onChange({ ...value, medicaidEnrollment })}
                    />
                </div>
            )}
        </>
    );
}

// 原型 mcdGroupMissing（10/19）：Set up your practice 不拦 Next，漏答的在 Review 上兜住——
// 某州没答，或答了 Yes 没填 group ID
export const medicaidGroupMissing = (states: string[], value?: PracticeRequirementsDto | null): string[] =>
    states.flatMap((st) => {
        const e = (value?.medicaid || []).find((m) => m.state === st);
        if (e?.enrolled === undefined || e?.enrolled === null)
            return [`${stateName(st)} Medicaid group enrollment not answered`];
        if (e.enrolled && !(e.certificationNumber || '').trim())
            return [`${stateName(st)} group Medicaid ID not provided`];
        return [];
    });
